import React from 'react';
import type { FieldError, Merge } from 'react-hook-form';
import type { NewContact } from '../../assets/icons/new-contact.tsx';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { cn } from '../../lib/utils.ts';

import { Button } from '../ui/button.tsx';

type IconType = typeof NewContact;

type ItemProps = {
    children: React.ReactNode | React.ReactNode[];
};

function Item({ children }: ItemProps) {
    const hasMultitple = React.Children.count(children) > 1;

    return (
        <div
            className={cn(
                'grid w-full flex-row items-center space-x-8',
                hasMultitple ? 'grid-cols-[90px_auto] xl:grid-cols-[160px_auto]' : '',
            )}
        >
            {children}
        </div>
    );
}

type TitleProps = {
    title: string | React.ReactNode | React.ReactNode[];
    subtitle: string | React.ReactNode | React.ReactNode[];
    icon?: IconType;
};

function Title({ title, subtitle, icon }: TitleProps) {
    const Icon = icon;
    return (
        <div className="flex w-full flex-row items-center border-b border-b-neutral-200 px-4 pb-4 pt-6">
            {Icon && (
                <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-lg border border-neutral-200 shadow-sm">
                    <Icon className="h-4 w-4" />
                </div>
            )}
            <div className="flex flex-col space-y-0.5">
                <div className="text-sm font-semibold text-neutral-900">{title}</div>
                <div className="text-sm font-normal text-neutral-600">{subtitle}</div>
            </div>
        </div>
    );
}

type LabelProps = {
    children: React.ReactNode | React.ReactNode[];
};

function Label({ children }: LabelProps) {
    return (
        <div className=" whitespace-pre-wrap text-right font-medium text-neutral-700 text-sm">{children}</div>
    );
}

type ItemListProps = {
    children: React.ReactNode | React.ReactNode[];
};

function ItemList({ children }: ItemListProps) {
    return <div className="flex w-full flex-col space-y-2.5 px-4 py-4">{children}</div>;
}

type BodyProps = {
    children: React.ReactNode | React.ReactNode[];
    className?: string;
};

function Body({ children, className = '' }: BodyProps) {
    return <div className={cn('', className)}>{children}</div>;
}

type SpacerProps = {
    className?: string;
};

function Spacer({ className = '' }: SpacerProps) {
    return <div className={cn('h-3', className)} />;
}

type ErrorProps = {
    errorMessage?: FieldError | Merge<FieldError, any>;
};

function Error({ errorMessage }: ErrorProps) {
    if (!errorMessage) {
        return null;
    }

    return (
        <>
            <div />
            <div className={cn('mt-0.5 pl-1 text-sm text-red-600')}>{errorMessage.message}</div>
        </>
    );
}

type SubmitBlockProps = {
    onCancel?: () => void;
    isLoading?: boolean;
    onClose?: () => void;
    onSubmit?: () => void;
    submitText?: string;
};

function SubmitBlock({ onClose, isLoading = false, onSubmit, submitText }: SubmitBlockProps) {
    const { t } = useLocale();

    return (
        <div className="flex w-full items-center justify-center border-t border-t-neutral-200">
            <div className="space-x-3 py-3">
                {onClose && (
                    <Button type="button" onClick={onClose} variant="subtle" className="min-w-24">
                        {t('form__cancel', { ns: 'common' })}
                    </Button>
                )}
                <Button
                    variant="primary"
                    isLoading={isLoading}
                    className="min-w-24"
                    type={onSubmit ? 'button' : 'submit'}
                    onClick={onSubmit}
                >
                    {submitText ? submitText : t('form__submit', { ns: 'common' })}
                </Button>
            </div>
        </div>
    );
}

type Props = {
    children: React.ReactNode | React.ReactNode[];
    className?: string;
};

export function ModalForm({ children, className }: Props) {
    return (
        <div className={cn('flex w-[518px] flex-col items-center justify-center', className)}>{children}</div>
    );
}

ModalForm.Title = Title;
ModalForm.ItemList = ItemList;
ModalForm.Item = Item;
ModalForm.Label = Label;
ModalForm.Body = Body;
ModalForm.Spacer = Spacer;
ModalForm.Error = Error;
ModalForm.SubmitBlock = SubmitBlock;
